import React, { useState, useEffect } from "react";
import { useApi } from "src/Api/useApi";
import { Client, NeurapulseTestTaker } from "src/@types/ImpiricaPlatformApi";
import { Button, Modal } from "antd";
import ListBox from "src/components/forms/ListBox";
import { toast } from "react-toastify";
import { LoadingIndicator } from "./LoadingIndicator";

interface IProps {
	organizationId: number;
	datasetId: number;
	clients: Client[];
	visible: boolean;
	onClose?: (allConflictsResolved?: boolean) => void;
}

const NeurapulseDuplicateClientsDialog: React.FC<IProps> = ({
	organizationId,
	datasetId,
	clients,
	visible,
	onClose,
}) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [clientsList, setClientsList] = useState<Client[]>(
		clients.sort((a, b) => {
			const nameA = `${a.firstName} ${a.lastName}`.toUpperCase();
			const nameB = `${b.firstName} ${b.lastName}`.toUpperCase();
			if (nameA < nameB) return -1;
			if (nameA > nameB) return 1;
			return 0;
		})
	);
	const [testTakers, setTestTakers] = useState<NeurapulseTestTaker[]>([]);
	const [testTakerOptions, setTestTakerOptions] = useState<
		{ id: string; name: string }[]
	>([]);
	const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
	const [selectedTestTakerId, setSelectedTestTakerId] = useState<number | null>(
		null
	);
	const [linkingError, setLinkingError] = useState<string | null>(null);

	const clientOptions = clientsList.map((client) => ({
		id: client.id.toString(),
		name: `${client.clientCode}: ${client.firstName} ${client.lastName} - ${client.birthYear}`,
	}));

	const { neurapulseClient } = useApi();

	useEffect(() => {
		const loadData = async () => {
			if (!neurapulseClient) return;
			const testTakers = await neurapulseClient.getTestTakers(
				organizationId,
				datasetId
			);

			const filteredClients = clientsList.filter((client) =>
				testTakers.some(
					(testTaker) =>
						client.firstName === testTaker.firstName &&
						client.lastName === testTaker.lastName &&
						client.birthYear === testTaker.yearOfBirth
				)
			);

			if (filteredClients.length === 0) {
				// No duplicates to resolve
				handleClose(true);
				return;
			}

			setClientsList(filteredClients);
			setTestTakers(testTakers);
			setLoading(false);
		};

		setLoading(true);
		loadData();
	}, [clients]);

	const handleClose = async (allConflictsResolved: boolean) => {
		if (onClose) {
			// Cleanup
			setSelectedClientId(null);
			setSelectedTestTakerId(null);
			setTestTakerOptions([]);
			onClose(allConflictsResolved);
		}
	};

	const onClientSelection = async (clientId: number) => {
		const client = clientsList.find((c) => c.id === clientId);
		if (!client) return;

		// Find matching test takers based on some criteria, e.g., first name
		const testTakerOptions = testTakers
			.filter(
				(x) =>
					x.firstName === client.firstName &&
					x.lastName === client.lastName &&
					x.yearOfBirth === client.birthYear
			)
			.map((testTaker) => ({
				id: testTaker.id.toString(),
				name: `${testTaker.email}: ${testTaker.firstName} ${testTaker.lastName} - ${testTaker.yearOfBirth}`,
			}));

		setTestTakerOptions(testTakerOptions);
	};

	const onLinkSelected = async () => {
		if (!neurapulseClient) return;

		try {
			if (selectedClientId && selectedTestTakerId) {
				await neurapulseClient.linkClientAndTestTaker(
					organizationId,
					datasetId,
					selectedTestTakerId,
					selectedClientId
				);

				// Get selected client
				const selectedClient = clientsList.find(
					(client) => client.id === selectedClientId
				);
				if (!selectedClient) return;

				// Remove linked client from the list
				const updatedClients = clientsList.filter(
					(client) =>
						client.firstName !== selectedClient.firstName &&
						client.lastName !== selectedClient.lastName &&
						client.birthYear !== selectedClient.birthYear
				);
				if (updatedClients.length === 0) {
					toast.success(
						"All duplicate test takers have been linked successfully."
					);
					handleClose(true);
				} else {
					// Set updated clients list
					setClientsList(updatedClients);
					// Update state to reflect the removed client
					setSelectedClientId(null);
					setSelectedTestTakerId(null);
					setTestTakerOptions([]);
				}

				setSelectedClientId(null);
				setSelectedTestTakerId(null);
			}
		} catch (error) {
			setLinkingError("Failed to link the selected client and test taker.");
		}
	};

	return (
		<Modal
			title="Duplicate Participants Found"
			width={700}
			visible={visible && clientsList.length > 0 && !loading}
			onCancel={() => handleClose(false)}
			footer={[
				<Button key="close" type="default" onClick={() => handleClose(false)}>
					Cancel
				</Button>,
				<Button
					key="linkSelected"
					type="primary"
					disabled={selectedClientId === null || selectedTestTakerId === null}
					onClick={onLinkSelected}
				>
					Link Selected
				</Button>,
			]}
		>
			<div>
				<h2>Duplicate Participants Found</h2>
				<p>
					The following participants are duplicates and were not linked to test
					takers. Please review and match them below:
				</p>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						gap: 20,
						flexDirection: "column",
						alignItems: "center",
						marginTop: 20,
					}}
				>
					<div>
						<div>
							<span style={{ fontWeight: "bold" }}>Select Participant:</span>
						</div>
						<ListBox
							options={clientOptions}
							style={{ height: 100, width: 600 }}
							selectedOption={
								selectedClientId
									? clientOptions.find(
											(option) => option.id === selectedClientId.toString()
									  ) || null
									: null
							}
							onSelect={(selectedClient) => {
								setSelectedClientId(
									selectedClient.id ? parseInt(selectedClient.id) : null
								);
								onClientSelection(
									selectedClient.id ? parseInt(selectedClient.id) : 0
								);
								setSelectedTestTakerId(null);
							}}
						/>
					</div>
					<div>
						<div>
							<span style={{ fontWeight: "bold" }}>
								Select Neurapulse Test Taker:
							</span>
						</div>
						<ListBox
							options={testTakerOptions}
							style={{ height: 100, width: 600 }}
							selectedOption={
								selectedTestTakerId
									? testTakerOptions.find(
											(option) => option.id === selectedTestTakerId.toString()
									  ) || null
									: null
							}
							onSelect={(selectedTestTaker) =>
								setSelectedTestTakerId(
									selectedTestTaker.id ? parseInt(selectedTestTaker.id) : null
								)
							}
						/>
					</div>
				</div>
				{linkingError && (
					<div style={{ marginTop: "10px" }}>{linkingError}</div>
				)}
			</div>
		</Modal>
	);
};

export default NeurapulseDuplicateClientsDialog;
